/**
 * Marketplace listings + offers client (hand-rolled).
 *
 * Transactional layer over the deployed /marketplace endpoints. Orval has NOT
 * been regenerated for these yet, so the DTOs + client functions are
 * hand-rolled on the shared axios mutator (src/api/client.ts → fseAxios),
 * mirroring src/api/photos.ts / provenance.ts. fseAxios unwraps `response.data`
 * and raises FseApiError on a non-2xx.
 *
 * MONEY: every *Micro field is a micro-ZARP (1e-6 ZARP) integer carried as a
 * STRING on the wire to preserve precision. NEVER Number() these for math —
 * display only, via microToZarp() + the <Money> kit (re-exported here for
 * convenience so screens import one money helper).
 *
 * Envelopes: the list endpoints return `{ data: [...], pagination }`; the
 * single-resource + mutation endpoints return the bare DTO.
 */
import { fseAxios } from './client';
import { microToZarp } from './market';

export { microToZarp };

// --------------------------------------------------------------------------
// Raw-bytes photo client (mirrors src/api/photos.ts)
// --------------------------------------------------------------------------
//
// The listing cover image streams raw image/* bytes behind auth, so it can't go
// through the JSON `fseAxios` mutator (it unwraps response.data / expects JSON)
// nor straight into `<img src>` (a plain <img> won't send the bearer). We mirror
// photos.ts: a built-in `fetch` with the Authorization header, then turn the
// response into a blob + object URL the caller revokes on cleanup.
//
// These two constants are kept in sync with src/api/client.ts (module-private
// there) exactly as photos.ts does — if client.ts changes the key/fallback,
// update both helpers.

const PHOTO_DEFAULT_BASE_URL = 'https://api.foodsecurityexchange.cloud';
const PHOTO_ACCESS_KEY = 'fse_access_token';

function resolvePhotoBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_FSE_API_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  return PHOTO_DEFAULT_BASE_URL;
}

function photoAuthHeader(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const token = window.localStorage.getItem(PHOTO_ACCESS_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Fetch the raw cover-image bytes for one listing with auth and turn them into
 * an object URL suitable for `<img src>`. The caller owns the URL and MUST
 * `URL.revokeObjectURL` it on cleanup to avoid leaking blobs. Throws on a
 * non-2xx (e.g. 404 when the listing has no photo) — callers should fall back to
 * the glyph placeholder on error.
 */
export async function fetchListingPhotoObjectUrl(listingId: string): Promise<string> {
  const res = await fetch(
    `${resolvePhotoBaseUrl()}/marketplace/listings/${listingId}/photo`,
    { method: 'GET', headers: { ...photoAuthHeader() } },
  );
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// --------------------------------------------------------------------------
// DTOs + request bodies / query — orval-generated (TD-124)
// --------------------------------------------------------------------------
//
// The DTOs, request bodies, and query are now the SINGLE source of truth in the
// generated client (regen vs live /docs-json). We re-export them under the names
// the screens already import, so a future field addition (e.g. sellerRegion)
// flows through a `pnpm --filter @fse/web api:gen` regen with no hand-edit. The
// client functions below stay hand-rolled because they return the unwrapped
// response body (fseAxios) that screens consume directly.

import type {
  ListingDto,
  ListingDtoSourceKind,
  ListingDtoStatus,
  OfferDto,
  OfferDtoStatus,
  ListingListDto,
  ListingListDtoPagination,
  OfferListDto,
  PublishListingDto,
  MarketplaceControllerListParams,
  ListingFacetsDto,
  CreateOfferDto,
} from './generated/models';

export type { ListingDto, OfferDto };
export type ListingSourceKind = ListingDtoSourceKind;
export type ListingStatus = ListingDtoStatus;
export type OfferStatus = OfferDtoStatus;
export type Pagination = ListingListDtoPagination;
export type ListingListResponse = ListingListDto;
export type OfferListResponse = OfferListDto;
export type PublishListingBody = PublishListingDto;
export type ListListingsQuery = MarketplaceControllerListParams;
export type ListingFacets = ListingFacetsDto;
export type CreateOfferBody = CreateOfferDto;

// --------------------------------------------------------------------------
// Listings
// --------------------------------------------------------------------------

/** Publish a listing from one of the caller's own batches / harvests (seller). */
export function publishListing(body: PublishListingBody): Promise<ListingDto> {
  return fseAxios<ListingDto>({
    url: '/marketplace/listings',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
  });
}

/**
 * List listings. With `mine: true` it scopes to the caller's own listings (the
 * My-listings seller view); without it, it's the public/buyer browse of active
 * listings. Optional filters: product / status / sourceKind, free-text `q`, the
 * `minPriceZarpPerKgMicro`/`maxPriceZarpPerKgMicro` band, and `grade` +
 * pagination. axios drops `undefined` params, so unset filters aren't sent.
 */
export function listListings(
  query: ListListingsQuery = {},
  signal?: AbortSignal,
): Promise<ListingListResponse> {
  return fseAxios<ListingListResponse>({
    url: '/marketplace/listings',
    method: 'GET',
    params: query,
    signal,
  });
}

/**
 * Fetch the browse filter facets (distinct grades + ask-price range across
 * active listings), optionally scoped to one product.
 */
export function getListingFacets(
  product?: string,
  signal?: AbortSignal,
): Promise<ListingFacets> {
  return fseAxios<ListingFacets>({
    url: '/marketplace/listings/facets',
    method: 'GET',
    params: product ? { product } : undefined,
    signal,
  });
}

/** Read a single listing by id. */
export function getListing(id: string, signal?: AbortSignal): Promise<ListingDto> {
  return fseAxios<ListingDto>({
    url: `/marketplace/listings/${id}`,
    method: 'GET',
    signal,
  });
}

/** Change a listing's status (seller): active ↔ paused, or withdrawn. */
export function patchListingStatus(id: string, status: ListingStatus): Promise<ListingDto> {
  return fseAxios<ListingDto>({
    url: `/marketplace/listings/${id}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: { status },
  });
}

// --------------------------------------------------------------------------
// Offers
// --------------------------------------------------------------------------

/**
 * Place an offer on a listing (verified buyer). A 403 with
 * `buyer_kyc_required` is surfaced gracefully by the screen (link to
 * verification); the server is the authority on FICA eligibility.
 */
export function createOffer(listingId: string, body: CreateOfferBody): Promise<OfferDto> {
  return fseAxios<OfferDto>({
    url: `/marketplace/listings/${listingId}/offers`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
  });
}

/** List the signed-in buyer's own offers (with listing context fields). */
export function listMyOffers(signal?: AbortSignal): Promise<OfferListResponse> {
  return fseAxios<OfferListResponse>({
    url: '/marketplace/offers',
    method: 'GET',
    signal,
  });
}

/** List the offers received on one of the seller's listings. */
export function listListingOffers(
  listingId: string,
  signal?: AbortSignal,
): Promise<OfferListResponse> {
  return fseAxios<OfferListResponse>({
    url: `/marketplace/listings/${listingId}/offers`,
    method: 'GET',
    signal,
  });
}

/** Accept an offer (seller). Mints the order server-side. */
export function acceptOffer(id: string): Promise<OfferDto> {
  return fseAxios<OfferDto>({
    url: `/marketplace/offers/${id}/accept`,
    method: 'PATCH',
  });
}

/** Reject an offer (seller). */
export function rejectOffer(id: string): Promise<OfferDto> {
  return fseAxios<OfferDto>({
    url: `/marketplace/offers/${id}/reject`,
    method: 'PATCH',
  });
}

/** Withdraw a pending offer (buyer). */
export function withdrawOffer(id: string): Promise<OfferDto> {
  return fseAxios<OfferDto>({
    url: `/marketplace/offers/${id}/withdraw`,
    method: 'PATCH',
  });
}
